import React, { useRef, useEffect, useMemo } from 'react'
import Dialog from '@mui/material/Dialog'
import { DialogContent, DialogTitle, DialogActions, Button } from '@mui/material'
import { Box } from '@mui/material'
import { AgGridReact } from 'ag-grid-react'
import ColorPickerCellEditor from 'components/common/CellEditors/ColorPickerCellEditor'
import { camelCaseToLabel } from 'utils/stringFunctions'
import { getStringId } from 'components/common/AgGridUtils'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

export const DEFAULT_ORG_THEME = {
  darkThemePrimaryCol: '#151515',
  lightThemePrimaryCol: '#f0f0f0',
  darkThemeAccentCol: '#c0c0c0',
  lightThemeAccentCol: '#ffffff',
  darkThemeLogoCol: '#5488B3',
  lightThemeLogoCol: '#f0f0f0',
  useSecondaryLogo: false,
  navBarGradientCols: '',
}

const THEME_FIELD_TYPES = {
  darkThemePrimaryCol: 'color',
  lightThemePrimaryCol: 'color',
  darkThemeAccentCol: 'color',
  lightThemeAccentCol: 'color',
  darkThemeLogoCol: 'color',
  lightThemeLogoCol: 'color',
  useSecondaryLogo: 'boolean',
  navBarGradientCols: 'text',
}

const ThemeModal = ({ orgTheme, onClose }) => {
  const _isMounted = useRef(false)
  const gridApi = useRef(null)
  const { getAgGridTheme } = useInnovaTheme()

  useEffect(() => {
    _isMounted.current = true
    return () => {
      _isMounted.current = false
    }
  }, [])

  const gridOptions = {
    rowSelection: {
      mode: 'singleRow',
      enableClickSelection: false,
      checkboxes: false,
    },
  }

  const getContextMenuItems = (params) => {
    return ['copy']
  }

  const getRowId = useMemo(() => {
    return (params) => getStringId(params.data?.id)
  }, [])

  const defaultColDef = useMemo(
    () => ({
      editable: true,
      headerClass: 'header-no-padding',
      filter: 'agSetColumnFilter',
      filterParams: { excelMode: 'windows' },
    }),
    [],
  )

  const onGridReady = (params) => {
    gridApi.current = params.api
    if (!orgTheme) return
    let rowData = []
    for (let key in DEFAULT_ORG_THEME) {
      let useDefault = false
      if (
        (THEME_FIELD_TYPES[key] !== 'boolean' && !orgTheme[key]) ||
        (Array.isArray(orgTheme[key]) && orgTheme[key].length === 0)
      ) {
        useDefault = true
      }

      if (THEME_FIELD_TYPES[key] === 'text' && Array.isArray(orgTheme[key])) {
        orgTheme[key] = orgTheme[key].join(',')
      }

      rowData.push({
        id: key,
        label: camelCaseToLabel(key),
        value: useDefault ? DEFAULT_ORG_THEME[key] : orgTheme[key],
        type: THEME_FIELD_TYPES[key],
        defaultValue: DEFAULT_ORG_THEME[key],
      })
    }
    params.api.setGridOption('rowData', rowData)
  }

  const autoSizeColumns = () => {
    if (!gridApi.current || gridApi.current.isDestroyed()) return
    gridApi.current.autoSizeAllColumns()
  }

  const onFirstDataRendered = (params) => {
    autoSizeColumns()
  }

  const getCellStyle = (params) => {
    if (params.data?.type === 'color') {
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        backgroundColor: params.value,
        color: params.value,
      }
    }
    return { display: 'block', paddingLeft: '0 !important', paddingRight: '0 !important' }
  }

  const GradientCellRenderer = ({ value }) => {
    if (!value) return null

    const colors = value.split(',').map((color) => color.trim())
    const gradientStops = colors.length === 1 ? [colors[0], colors[0]] : colors
    const gradient = `linear-gradient(90deg, ${gradientStops.join(', ')})`

    return (
      <Box
        sx={{
          background: gradient,
          paddingLeft: 1,
        }}>
        {colors.join(', ')}
      </Box>
    )
  }

  const components = useMemo(
    () => ({
      gradientCellRenderer: GradientCellRenderer,
    }),
    [],
  )

  const getCellRenderer = (params) => {
    if (params.data?.id === 'navBarGradientCols') {
      return { component: 'gradientCellRenderer' }
    }
    return params.data?.type === 'boolean' ? { component: 'agCheckboxCellRenderer' } : undefined
  }

  const getCellEditor = (params) => {
    if (params.data?.type === 'boolean') {
      return { component: 'agCheckboxCellEditor' }
    }

    if (params.data?.type === 'color') {
      return {
        component: ColorPickerCellEditor,
        params: { color: params.value, defaultColor: params.data?.defaultValue },
        popup: true,
      }
    }
    return { component: 'agTextCellEditor' }
  }

  const columnDefs = useMemo(
    () => [
      {
        field: 'label',
        colId: 'label',
        headerName: 'Label',
        editable: false,
      },
      {
        headerName: '',
        field: 'value',
        colId: 'value',
        minWidth: 350,
        editable: true,
        cellRendererSelector: getCellRenderer,
        cellStyle: getCellStyle,
        cellEditorSelector: getCellEditor,
        valueSetter: (params) => {
          params.data.value = params.newValue
          return true
        },
        valueGetter: (params) => {
          return params.data.value
        },
      },
    ],
    [],
  )

  const handleSave = async () => {
    let newThemeSettings = {
      darkThemePrimaryCol: '',
      lightThemePrimaryCol: '',
      darkThemeAccentCol: '',
      lightThemeAccentCol: '',
      darkThemeLogoCol: '',
      lightThemeLogoCol: '',
      useSecondaryLogo: false,
      navBarGradientCols: '',
    }

    gridApi.current.forEachNode((node) => {
      if (!node.data) return
      let key = node.data.id
      let value = node.data.value

      if (DEFAULT_ORG_THEME.hasOwnProperty(key) && DEFAULT_ORG_THEME[key] === value) return

      newThemeSettings[key] = value
    })

    onClose(newThemeSettings)
  }

  const handleReset = () => {
    gridApi.current.forEachNode((node) => {
      if (!node.data) return
      let key = node.data.id
      if (DEFAULT_ORG_THEME.hasOwnProperty(key)) {
        node.setDataValue('value', DEFAULT_ORG_THEME[key])
      }
    })
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth='xl'
      PaperProps={{
        sx: {
          width: '90vw',
          height: '80vh',
          backgroundColor: 'itemBackground',
        },
      }}>
      <DialogTitle>{`Organization Theme Settings`}</DialogTitle>
      <DialogContent style={{ overflow: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: 'calc(100% - 15px)',
            maxHeight: '100%',
          }}>
          <div className={getAgGridTheme()} style={{ width: '100%', height: '100%' }}>
            <AgGridReact
              components={components}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              headerHeight={0}
              getRowId={getRowId}
              onGridReady={onGridReady}
              onFirstDataRendered={onFirstDataRendered}
              getContextMenuItems={getContextMenuItems}
            />
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={handleReset} color='primary'>
          Default
        </Button>
        <Button variant='outlined' onClick={handleSave} color='primary'>
          Apply
        </Button>
        <Button variant='outlined' onClick={() => onClose(null)} color='secondary'>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ThemeModal
